import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, ArrowLeft, ChevronRight, Bell, Plus, Dumbbell, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

// ─── Feedback Item (treino) ───
const MSG_CACHE = 'ferraz_messages_cache';

const FeedbackItem = ({ feedback, onClick }) => {
  const hasUnread = feedback.has_unread_reply;
  return (
    <motion.div
      onClick={onClick}
      className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer hover:bg-zinc-900/80 transition-all ${hasUnread ? 'border-neon-purple/50' : 'border-white/5'}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="feedback-item"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Dumbbell className="w-4 h-4 text-neon-lime" />
          <span className="text-sm font-bold text-white">{feedback.day_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <span className="flex items-center gap-1 text-xs bg-neon-purple/20 text-neon-purple px-2 py-1 rounded-full">
              <Bell className="w-3 h-3" /> Nova resposta
            </span>
          )}
          <span className="text-xs text-zinc-500">
            {feedback.feedback_at && new Date(feedback.feedback_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          </span>
        </div>
      </div>
      <p className="text-zinc-400 text-sm line-clamp-2">{feedback.feedback}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-zinc-500">
          {feedback.reply_count > 0 ? `${feedback.reply_count} mensagem(ns)` : 'Sem respostas'}
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </div>
    </motion.div>
  );
};

// ─── Message Item (direct) ───
const MessageItem = ({ message, onClick }) => {
  const hasUnread = message.has_unread_reply || (message.type === 'admin_message' && !message.read_by_user);
  const isFromAdmin = message.type === 'admin_message';
  return (
    <motion.div
      onClick={onClick}
      className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer hover:bg-zinc-900/80 transition-all ${hasUnread ? 'border-neon-purple/50' : 'border-white/5'}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="message-item"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mail className={`w-4 h-4 ${isFromAdmin ? 'text-neon-lime' : 'text-neon-purple'}`} />
          <span className="text-sm font-bold text-white">{message.subject}</span>
          {isFromAdmin && (
            <span className="text-xs bg-neon-lime/20 text-neon-lime px-1.5 py-0.5 rounded">Treinador</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <span className="flex items-center gap-1 text-xs bg-neon-purple/20 text-neon-purple px-2 py-1 rounded-full">
              <Bell className="w-3 h-3" /> {isFromAdmin && !message.read_by_user ? 'Nova' : 'Nova resposta'}
            </span>
          )}
          <span className="text-xs text-zinc-500">
            {new Date(message.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          </span>
        </div>
      </div>
      <p className="text-zinc-400 text-sm line-clamp-2">{message.message}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-zinc-500">
          {message.reply_count > 0 ? `${message.reply_count} mensagem(ns)` : isFromAdmin ? 'Do treinador' : 'Aguardando resposta'}
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </div>
    </motion.div>
  );
};

// ─── Conversation View (shared for feedbacks and messages) ───
const ConversationView = ({ item, type, onBack, onRefresh }) => {
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const endpoint = type === 'feedback'
    ? `${API}/user/feedbacks/${item.id}`
    : `${API}/user/messages/${item.id}`;

  const replyEndpoint = type === 'feedback'
    ? `${API}/user/feedbacks/${item.id}/reply`
    : `${API}/user/messages/${item.id}/reply`;

  const loadDetail = useCallback(async () => {
    try {
      const res = await axios.get(endpoint);
      setDetail(res.data);
    } catch (e) {
      toast.error('Erro ao carregar conversa');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(replyEndpoint, { message: replyText });
      toast.success('Resposta enviada!');
      setReplyText('');
      loadDetail();
      onRefresh();
    } catch (e) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-neon-purple" />
      </div>
    );
  }

  const replies = detail?.replies || [];
  const title = type === 'feedback' ? (detail?.day_name || 'Treino') : (detail?.subject || 'Mensagem');
  const originalMessage = type === 'feedback' ? detail?.feedback : detail?.message;
  const dateStr = type === 'feedback' ? detail?.feedback_at : detail?.created_at;
  const isAdminMessage = detail?.type === 'admin_message';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 mb-4">
        <Button onClick={onBack} variant="ghost" className="text-zinc-400 hover:text-white p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            {type === 'feedback' ? <Dumbbell className="w-4 h-4 text-neon-lime" /> : <Mail className="w-4 h-4 text-neon-purple" />}
            <h3 className="font-bold text-white">{title}</h3>
          </div>
          <p className="text-xs text-zinc-500">
            {dateStr && new Date(dateStr).toLocaleDateString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pb-4">
        {/* Original message */}
        <div className={`flex ${isAdminMessage ? 'justify-start' : 'justify-end'}`}>
          <div className={`rounded-lg p-3 max-w-[85%] ${isAdminMessage ? 'bg-zinc-800' : 'bg-neon-lime/20'}`}>
            <p className={`text-sm ${isAdminMessage ? 'text-zinc-300' : 'text-neon-lime'}`}>{originalMessage}</p>
            <p className="text-xs text-zinc-500 mt-1 text-right">
              {isAdminMessage ? (detail?.from_admin_name || 'Treinador') : 'Você'}
            </p>
          </div>
        </div>

        {replies.map((reply) => (
          <div key={reply.id} className={`flex ${reply.author_type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`rounded-lg p-3 max-w-[85%] ${reply.author_type === 'user' ? 'bg-neon-lime/20' : 'bg-zinc-800'}`}>
              <p className={`text-sm ${reply.author_type === 'user' ? 'text-neon-lime' : 'text-zinc-300'}`}>
                {reply.message}
              </p>
              <p className="text-xs text-zinc-500 mt-1 text-right">
                {reply.author_type === 'user' ? 'Você' : reply.author_name} • {new Date(reply.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-4 border-t border-white/5">
        <Input
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Digite sua resposta..."
          className="flex-1 bg-zinc-900 border-zinc-800 text-white"
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
          data-testid="reply-input"
        />
        <Button onClick={handleSendReply} disabled={sending || !replyText.trim()} className="bg-neon-lime text-black" data-testid="send-reply-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── New Message Form ───
const NewMessageForm = ({ onSend, onCancel }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Preencha o assunto e a mensagem');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/user/messages`, { subject: subject.trim(), message: message.trim() });
      toast.success('Mensagem enviada ao treinador!');
      onSend();
    } catch (e) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Button onClick={onCancel} variant="ghost" className="text-zinc-400 hover:text-white p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="font-primary font-bold text-xl text-white uppercase">Nova Mensagem</h2>
          <p className="text-zinc-500 text-sm">Envie uma mensagem para seu treinador</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm text-zinc-400 mb-1 block">Assunto</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex: Dúvida sobre meu treino, Dor no joelho..."
            className="bg-zinc-900 border-zinc-800 text-white"
            data-testid="message-subject-input"
          />
        </div>
        <div>
          <label className="text-sm text-zinc-400 mb-1 block">Mensagem</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escreva sua mensagem para o treinador..."
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-neon-purple/50 text-white rounded-lg p-3 min-h-[120px] resize-none placeholder:text-zinc-600 text-sm focus:outline-none"
            data-testid="message-body-input"
          />
        </div>
        <Button
          onClick={handleSubmit}
          disabled={sending || !subject.trim() || !message.trim()}
          className="w-full bg-neon-lime text-black font-bold hover:bg-neon-lime/80 disabled:opacity-50"
          data-testid="send-message-btn"
        >
          {sending ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
              Enviando...
            </div>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Enviar Mensagem
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Page ───
export const MyFeedbacksPage = ({ user }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null); // { data, type: 'feedback'|'message' }
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('all');


  const fetchAll = useCallback(async () => {
    try {
      const [fbRes, msgRes] = await Promise.all([
        axios.get(`${API}/user/feedbacks`),
        axios.get(`${API}/user/messages`),
      ]);
      setFeedbacks(fbRes.data);
      setMessages(msgRes.data);
      try { localStorage.setItem(MSG_CACHE, JSON.stringify({ fb: fbRes.data, msg: msgRes.data })); } catch (e) { console.warn('[MyFeedbacksPage] non-critical failure', e); }
    } catch (e) {
      console.error(e);
      // Load from cache
      try {
        const c = JSON.parse(localStorage.getItem(MSG_CACHE));
        if (c) {
          setFeedbacks((prev) => (prev.length ? prev : c.fb || []));
          setMessages((prev) => (prev.length ? prev : c.msg || []));
        }
      } catch (err) { console.warn('[MyFeedbacksPage] non-critical failure', err); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load cache first
    try { const c = JSON.parse(localStorage.getItem(MSG_CACHE)); if (c) { setFeedbacks(c.fb || []); setMessages(c.msg || []); setLoading(false); } } catch (e) { console.warn('[MyFeedbacksPage] non-critical failure', e); }
    fetchAll();
  }, [fetchAll]);

  if (loading && feedbacks.length === 0 && messages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-lime" />
      </div>
    );
  }

  // Combine and sort all items
  const allItems = [
    ...feedbacks.map(f => ({ data: f, type: 'feedback', date: f.feedback_at || f.completed_at })),
    ...messages.map(m => ({ data: m, type: 'message', date: m.created_at })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const filteredItems = activeTab === 'all'
    ? allItems
    : activeTab === 'messages'
    ? allItems.filter(i => i.type === 'message')
    : allItems.filter(i => i.type === 'feedback');

  const totalUnread = [
    ...feedbacks.filter(f => f.has_unread_reply),
    ...messages.filter(m => m.has_unread_reply),
  ].length;

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 md:px-6 pt-6 md:pt-8">
      <AnimatePresence mode="wait">
        {selectedItem ? (
          <motion.div
            key="conversation"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="h-[calc(100vh-12rem)]"
          >
            <ConversationView
              item={selectedItem.data}
              type={selectedItem.type}
              onBack={() => setSelectedItem(null)}
              onRefresh={fetchAll}
            />
          </motion.div>
        ) : showNewMessage ? (
          <motion.div
            key="new-message"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <NewMessageForm
              onSend={() => { setShowNewMessage(false); fetchAll(); }}
              onCancel={() => setShowNewMessage(false)}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-neon-purple" />
                <div>
                  <h1 className="font-primary font-black text-2xl text-white uppercase">Mensagens</h1>
                  <p className="text-zinc-500 text-sm">Conversas com seu treinador</p>
                </div>
              </div>
              <Button
                onClick={() => setShowNewMessage(true)}
                className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
                data-testid="new-message-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Nova Mensagem</span>
                <span className="sm:hidden">Nova</span>
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              {[
                { id: 'all', label: 'Todas' },
                { id: 'messages', label: 'Mensagens' },
                { id: 'feedbacks', label: 'Treinos' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-neon-lime text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* List */}
            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h2 className="font-bold text-xl text-white mb-2">Nenhuma conversa ainda</h2>
                <p className="text-zinc-500 mb-4">Envie uma mensagem para seu treinador</p>
                <Button
                  onClick={() => setShowNewMessage(true)}
                  className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Enviar Mensagem
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredItems.map((item) =>
                  item.type === 'feedback' ? (
                    <FeedbackItem
                      key={`fb-${item.data.id}`}
                      feedback={item.data}
                      onClick={() => setSelectedItem(item)}
                    />
                  ) : (
                    <MessageItem
                      key={`msg-${item.data.id}`}
                      message={item.data}
                      onClick={() => setSelectedItem(item)}
                    />
                  )
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
